import { z } from 'zod';
import {
  formatMoneyDisplay,
  type OrgFundraiserCreate,
  type OrgFundraiserOut,
  type OrgFundraiserUpdate,
} from '@givmo/api-client';

export const NAME_MAX = 100;
const DESCRIPTION_MAX = 2000;
export const STORY_MAX = 20000;

export const NAME_MESSAGE = 'Give your fundraiser a name';
export const GOAL_MESSAGE = 'Enter a positive dollar amount (e.g. 5000 or 5000.00)';

// ── Goal handling (MONEY LAW: string in, string out — no float) ─────────────────────────────────────

/** Strip `$`, commas, and spaces from a goal input, leaving a plain decimal string. NEVER parses a float. */
export function cleanGoal(raw: string): string {
  return raw.replace(/[$,\s]/g, '');
}

/** A goal input is valid when empty (no goal / clears) OR a positive decimal with ≤2 places (string test). */
export function isGoalish(raw: string): boolean {
  const cleaned = cleanGoal(raw);
  if (cleaned.length === 0) return true;
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return false;
  return /[1-9]/.test(cleaned); // strictly positive without a float round-trip
}

/**
 * A goal as people write it, through the module's money form: thousands separators, and cents only
 * when there are some (the server's `"60000.00"` → `"60,000"`, `"60000.50"` → `"60,000.50"`; EMC-WEB7's
 * production finding). Empty for no goal; a value that isn't a plain decimal stays as the wire has it.
 */
export function displayGoal(amount: string | null | undefined): string {
  if (!amount) return '';
  return formatMoneyDisplay(amount, { cents: 'when-present' }) ?? amount;
}

/**
 * One goal however it is written (`"60,000"`, `"60000"` and `"60000.00"` all → `"60000.00"`), so a
 * form showing {@link displayGoal}'s words is not a change from the server's. A value that isn't a
 * plain decimal is compared as typed (its schema error holds the save back).
 */
export function canonicalGoal(raw: string): string {
  const cleaned = cleanGoal(raw);
  const match = /^(\d+)(?:\.(\d*))?$/.exec(cleaned);
  if (!match) return cleaned;
  const whole = (match[1] ?? '').replace(/^0+(?=\d)/, '');
  return `${whole}.${(match[2] ?? '').padEnd(2, '0')}`;
}

// ── Form model ──────────────────────────────────────────────────────────────────────────────────────

export const fundraiserSchema = z.object({
  name: z.string().trim().min(1, NAME_MESSAGE).max(NAME_MAX),
  goal: z.string().refine(isGoalish, { message: GOAL_MESSAGE }),
  description: z.string().max(DESCRIPTION_MAX),
  story: z.string().max(STORY_MAX),
  showStatistics: z.boolean(),
});
export type FundraiserFormValues = z.infer<typeof fundraiserSchema>;

export function emptyForm(): FundraiserFormValues {
  return { name: '', goal: '', description: '', story: '', showStatistics: true };
}

/** Seed the form from a loaded fundraiser — the server goal is shown as people write it ({@link displayGoal}). */
export function fundraiserToForm(fundraiser: OrgFundraiserOut): FundraiserFormValues {
  return {
    name: fundraiser.name ?? '',
    goal: displayGoal(fundraiser.goal_amount),
    description: fundraiser.description ?? '',
    story: fundraiser.story ?? '',
    showStatistics: fundraiser.show_statistics,
  };
}

/** Build the CREATE body — name required; other fields included only when non-empty. */
export function buildCreateBody(values: FundraiserFormValues): OrgFundraiserCreate {
  const goal = cleanGoal(values.goal);
  const description = values.description.trim();
  const story = values.story.trim();
  return {
    name: values.name.trim(),
    show_statistics: values.showStatistics,
    ...(goal.length > 0 ? { goal_amount: goal } : {}),
    ...(description.length > 0 ? { description } : {}),
    ...(story.length > 0 ? { story } : {}),
  };
}

/**
 * Build the MINIMAL PATCH: only fields that CHANGED from the loaded fundraiser are included; an emptied
 * text field / goal becomes `null` (clear). Returns `null` when nothing changed (so we can no-op).
 */
export function buildUpdatePatch(
  fundraiser: OrgFundraiserOut,
  values: FundraiserFormValues,
): OrgFundraiserUpdate | null {
  const patch: OrgFundraiserUpdate = {};
  let changed = false;

  const name = values.name.trim();
  if (name !== (fundraiser.name ?? '')) {
    patch.name = name;
    changed = true;
  }

  const description = values.description.trim();
  if (description !== (fundraiser.description ?? '')) {
    patch.description = description.length > 0 ? description : null;
    changed = true;
  }

  const story = values.story.trim();
  if (story !== (fundraiser.story ?? '')) {
    patch.story = story.length > 0 ? story : null;
    changed = true;
  }

  const goal = cleanGoal(values.goal);
  // Compared as amounts: the form shows the server's "60000.00" as "60,000", which is no change.
  if (canonicalGoal(goal) !== canonicalGoal(fundraiser.goal_amount ?? '')) {
    patch.goal_amount = goal.length > 0 ? goal : null;
    changed = true;
  }

  if (values.showStatistics !== fundraiser.show_statistics) {
    patch.show_statistics = values.showStatistics;
    changed = true;
  }

  return changed ? patch : null;
}
